import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useRefresh } from '../../contexts/RefreshContext';

const THRESHOLD = 72;   // px of pull needed to trigger refresh
const RESISTANCE = 0.45; // how much the indicator resists the drag (< 1 = slower than finger)

/**
 * PullToRefresh — wraps a scrollable page and adds iOS-style pull-to-refresh.
 *
 * Props:
 *   onRefresh  — async function called when the user pulls past the threshold
 *   children   — page content
 *   disabled   — set true to disable (e.g. while data is loading for the first time)
 */
export default function PullToRefresh({ onRefresh, children, disabled = false }) {
  const ctx = useRefresh();
  // If no onRefresh prop is given, use the context's registered page handler
  const resolvedRefresh = onRefresh ?? ctx?.refresh;
  const [pullY, setPullY] = useState(0);       // current pull distance (post-resistance)
  const [refreshing, setRefreshing] = useState(false);

  const startYRef = useRef(null);
  const pullingRef = useRef(false);
  const thresholdCrossedRef = useRef(false);

  const trigger = useCallback(async () => {
    if (!resolvedRefresh) return;
    setRefreshing(true);
    try {
      await resolvedRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [resolvedRefresh]);

  useEffect(() => {
    if (disabled) return;

    const onTouchStart = (e) => {
      if (window.scrollY === 0) {
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = false;
        thresholdCrossedRef.current = false;
      }
    };

    const onTouchMove = (e) => {
      if (startYRef.current === null || refreshing) return;
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0 || window.scrollY > 0) {
        startYRef.current = null;
        setPullY(0);
        return;
      }
      pullingRef.current = true;
      const distance = Math.min(delta * RESISTANCE, THRESHOLD * 1.6);
      setPullY(distance);

      if (distance >= THRESHOLD && !thresholdCrossedRef.current) {
        thresholdCrossedRef.current = true;
      }
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) return;
      if (thresholdCrossedRef.current && !refreshing) {
        setPullY(THRESHOLD * 0.75); // hold indicator while refreshing
        trigger();
      } else {
        setPullY(0);
      }
      startYRef.current = null;
      pullingRef.current = false;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [disabled, refreshing, trigger]);

  // Collapse indicator once refresh completes
  useEffect(() => {
    if (!refreshing) setPullY(0);
  }, [refreshing]);

  const progress = Math.min(pullY / THRESHOLD, 1);
  const isTriggered = progress >= 1 || refreshing;

  return (
    <div style={{ position: 'relative' }}>
      {/* Pull indicator */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          height: `${pullY}px`,
          overflow: 'hidden',
          transition: (pullingRef.current && !refreshing) ? 'none' : 'height 0.3s ease',
          pointerEvents: 'none',
        }}
      >
        {pullY > 8 && (
          <div
            style={{
              marginBottom: '8px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(59,130,246,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              style={{
                transform: isTriggered ? 'none' : `rotate(${progress * 270}deg)`,
                animation: isTriggered ? 'ptr-spin 0.7s linear infinite' : 'none',
                transition: 'transform 0.1s',
              }}
            >
              <circle cx="9" cy="9" r="7" stroke="#3b82f6" strokeWidth="2" strokeOpacity="0.3" />
              <path
                d="M9 2 A7 7 0 0 1 16 9"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
                strokeDasharray={`${progress * 44} 44`}
              />
            </svg>
          </div>
        )}
      </div>

      {children}

      <style>{`
        @keyframes ptr-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
